
import { supabase } from "@/integrations/supabase/client";
import { Json } from "@/integrations/supabase/types/common";

interface ResourceMetrics {
  cpuUsage: number;
  memoryUsage: number;
  taskCount: number;
}

interface ResourceLimits {
  maxCpuUsage: number;
  maxMemoryUsage: number;
  maxConcurrentTasks: number;
}

export class ResourceOptimizationManager {
  private readonly agentId: string;
  private resourceLimits: ResourceLimits;
  private static WORKFLOW_CONTROL_ID = '67e2e098-d495-4c63-a656-4ebb2d46e2df';

  constructor(agentId: string = ResourceOptimizationManager.WORKFLOW_CONTROL_ID) {
    this.agentId = agentId;
    this.resourceLimits = {
      maxCpuUsage: 80,
      maxMemoryUsage: 70,
      maxConcurrentTasks: 5
    };
    this.loadResourceLimits();
  }

  private async loadResourceLimits(): Promise<void> {
    try {
      const { data: agent } = await supabase
        .from('agent_types')
        .select('resource_limits')
        .eq('id', this.agentId)
        .single();

      if (agent?.resource_limits && typeof agent.resource_limits === 'object') {
        const limits = agent.resource_limits as Record<string, number>;
        this.resourceLimits = {
          maxCpuUsage: limits.max_cpu_usage ?? this.resourceLimits.maxCpuUsage,
          maxMemoryUsage: limits.max_memory_usage ?? this.resourceLimits.maxMemoryUsage,
          maxConcurrentTasks: limits.max_concurrent_tasks ?? this.resourceLimits.maxConcurrentTasks
        };
      }
    } catch (error) {
      console.error('Error loading resource limits:', error);
    }
  }

  async trackResourceUsage(metrics: ResourceMetrics): Promise<void> {
    await supabase.from('resource_allocation').insert({
      agent_id: this.agentId,
      resource_type: 'system',
      allocated_amount: metrics.taskCount,
      utilization_percentage: Math.max(metrics.cpuUsage, metrics.memoryUsage),
      optimization_metadata: {
        cpu_usage: metrics.cpuUsage,
        memory_usage: metrics.memoryUsage,
        task_count: metrics.taskCount,
        timestamp: new Date().toISOString()
      }
    });

    // Update agent state with current metrics
    await supabase
      .from('agent_states')
      .update({
        load_metrics: {
          cpu_usage: metrics.cpuUsage,
          memory_usage: metrics.memoryUsage,
          task_count: metrics.taskCount
        },
        updated_at: new Date().toISOString()
      })
      .eq('agent_id', this.agentId);

    this.checkResourceThresholds(metrics);
  }

  private async checkResourceThresholds(metrics: ResourceMetrics): Promise<void> {
    const isOverloaded = 
      metrics.cpuUsage > this.resourceLimits.maxCpuUsage ||
      metrics.memoryUsage > this.resourceLimits.maxMemoryUsage ||
      metrics.taskCount > this.resourceLimits.maxConcurrentTasks;

    if (isOverloaded) {
      await this.handleResourceOverload(metrics);
    }
  }

  private async handleResourceOverload(metrics: ResourceMetrics): Promise<void> {
    // Log overload event
    await supabase.from('system_metrics').insert({
      metric_type: 'resource_overload',
      metric_value: {
        cpu_usage: metrics.cpuUsage,
        memory_usage: metrics.memoryUsage,
        task_count: metrics.taskCount
      },
      labels: {
        agent_id: this.agentId,
        severity: 'warning'
      }
    });

    // Update agent state to reflect overload
    await supabase
      .from('agent_states')
      .update({
        current_status: 'overloaded',
        health_metrics: {
          cpu_usage: metrics.cpuUsage,
          memory_usage: metrics.memoryUsage,
          last_overload: new Date().toISOString()
        }
      })
      .eq('agent_id', this.agentId);
  }

  async optimizeResourceAllocation(): Promise<void> {
    const { data: metrics } = await supabase
      .from('resource_allocation')
      .select('*')
      .eq('agent_id', this.agentId)
      .order('created_at', { ascending: false })
      .limit(10);

    if (!metrics?.length) return;

    const averageUtilization = metrics.reduce(
      (sum, m) => sum + (m.utilization_percentage || 0), 
      0
    ) / metrics.length;

    const scalingFactor = averageUtilization > 70 ? 0.8 : 1.2;

    await supabase
      .from('agent_states')
      .update({
        scaling_metrics: {
          last_scaled: new Date().toISOString(),
          scale_factor: scalingFactor,
          average_utilization: averageUtilization
        }
      })
      .eq('agent_id', this.agentId);
  }
}
